import React from 'react';
import type { PublisherConfig, SelfServiceSubmissionRow } from '../../types/selfService';
import { formatDate, formatDateTime } from './shared/dateFormat';
import { formatMoney } from './shared/format';
import { receiptNumberFor, formatCardBrand, receiptTotalsFor } from './orderReceiptHelpers';
import {
  buildMonthlyReceiptSchedule,
  buildUpfrontPayments,
  paymentStatusLabel,
  type ReceiptExtensionCharge,
  type ReceiptPaymentStatus,
} from './receiptPayments';

export interface OrderReceiptProps {
  row: SelfServiceSubmissionRow;
  config: PublisherConfig;
  // CTV-516: upfront extension charges (from the submission's edit log) so the
  // receipt can list each extension as its own payment row. Monthly extensions
  // are reflected in the live installment schedule instead, so they are not
  // passed here. Omitted on surfaces with no edits (e.g. the post-submit page).
  extensionCharges?: ReceiptExtensionCharge[];
}

// Small status pill shown beside dated payment rows. Uniform slate to match the
// surrounding meta text — the label alone communicates the status.
const StatusPill: React.FC<{ status: ReceiptPaymentStatus }> = ({ status }) => (
  <span className="text-[10px] font-semibold uppercase tracking-[0.08em] text-slate-500">
    {paymentStatusLabel(status)}
  </span>
);

// Status-aware sub-line under the upfront total. The authorized (not-yet-
// captured) copy is kept verbatim — it's the common at-submission state.
function upfrontSummaryNote(
  status: SelfServiceSubmissionRow['status'],
  reviewedAt: string | null,
): string {
  switch (status) {
    case 'approved':
      return reviewedAt ? `Charged ${formatDate(reviewedAt)}.` : 'Charged on approval.';
    case 'payment_authorized':
      return 'Authorized today; charged on approval.';
    case 'payment_failed':
      return 'This payment could not be completed.';
    case 'payment_refunded':
      return reviewedAt ? `Refunded ${formatDate(reviewedAt)}.` : 'Refunded.';
    case 'creative_rejected':
    case 'archived':
    case 'canceled':
      return 'Hold released — not charged.';
    default:
      return 'Awaiting payment.';
  }
}

const Row: React.FC<{ label: string; value: React.ReactNode }> = ({ label, value }) => (
  <div className="grid grid-cols-[160px_1fr] gap-3 py-1.5 text-sm">
    <div className="text-slate-500">{label}</div>
    <div className="text-slate-900">{value}</div>
  </div>
);

const SectionTitle: React.FC<{ children: React.ReactNode }> = ({ children }) => (
  <div className="text-[11px] font-semibold uppercase tracking-wider text-slate-500 mb-2">
    {children}
  </div>
);

const OrderReceipt: React.FC<OrderReceiptProps> = ({ row, config, extensionCharges }) => {
  // CTV-504: for monthly campaigns prefer the live per-installment schedule
  // (post-extension state) over the frozen at-submission amount_cents. The
  // committed totals are then sums across installments — so an extension
  // that bumped the tail from $21 to $840 shows up everywhere instead of the
  // receipt rendering the original $362 split.
  // CTV-518: totals always render backend cents (see receiptTotalsFor) —
  // media spend = budget, total = budget + tax + service fee.
  const { total, serviceFee: fee, tax, mediaSpend } = receiptTotalsFor(row);

  // Receipt targeting display. The API parses target_geographies (a
  // comma-separated TEXT column written by serializeTargetGeographies) into
  // campaign.targetMarkets — a flat list of codes whose format depends on
  // the geo tab the buyer used:
  //   - 5-digit codes → ZIPs (radius or manual ZIP tab)
  //   - 2-letter codes → US state codes
  //   - else (3-digit numeric / mixed) → DMA codes
  // We sniff the format the same way the dashboard does and render a
  // concise summary. The "Channel: <name>" label is reserved for the
  // legacy / default-mode case where target_geographies is empty — once
  // the buyer picks ANY codes (including a DMA that happens to match the
  // publisher's own default), we show what they picked.
  const isSingleMarket = config.markets.length === 1;
  const targeted = row.campaign?.targetMarkets ?? [];
  const summary = (() => {
    if (targeted.length === 0) return null;
    const allZip = targeted.every((c) => /^\d{5}$/.test(c));
    if (allZip) {
      return { label: 'Targeting', value: `${targeted.length} ZIP Code${targeted.length === 1 ? '' : 's'}` };
    }
    const allState = targeted.every((c) => /^[A-Z]{2}$/.test(c));
    if (allState) {
      return { label: 'Targeting', value: targeted.join(', ') };
    }
    // Treat the rest as DMA codes; resolve names from the publisher's
    // configured markets when possible, otherwise show the raw code.
    const names = targeted
      .map((code) => config.markets.find((c) => c.dmaCode === code)?.dmaName ?? code)
      .filter(Boolean);
    return { label: 'Markets', value: names.join(', ') };
  })();
  const marketsLabel = summary?.label ?? (isSingleMarket ? 'Channel' : 'Markets');
  const marketsValue = summary?.value ?? (() => {
    if (isSingleMarket) {
      const m = config.markets[0];
      return m.channels.join(', ') || m.dmaName || '—';
    }
    return '—';
  })();

  const isMonthly = row.payment_plan === 'monthly';
  const cardBrand = row.payment_method_brand;
  const cardLast4 = row.payment_method_last4;
  const cardLine = cardBrand && cardLast4
    ? `${isMonthly ? 'Card on file:' : 'Paid with'} ${formatCardBrand(cardBrand)} ending in ${cardLast4}`
    : null;
  // CTV-516: monthly schedule rows carry each month's status + actual date
  // (paid date when captured, scheduled invoice date otherwise). Upfront
  // campaigns get a payments ledger: the initial charge plus each extension as
  // its own row, summing to the original authorized total + charged extensions.
  // CTV-518: amounts come straight from row.installments, which the backend
  // writes with the MoR per-installment service-fee gross-up.
  const schedule = isMonthly ? buildMonthlyReceiptSchedule(row) : [];
  const upfrontPayments = isMonthly
    ? null
    : buildUpfrontPayments(row, extensionCharges ?? []);
  const hasUpfrontExtensions = (upfrontPayments?.rows.length ?? 0) > 1;

  const planLabel = isMonthly ? 'Monthly' : 'Upfront';
  const businessName = row.business_info?.businessName?.trim() || '—';
  const contactName = row.business_info?.contactName?.trim();
  const contactEmail = row.contact_email?.trim();
  const contactPhone = row.business_info?.contactPhone?.trim();

  return (
    <div
      className="bg-white text-slate-900 border border-slate-200 rounded-lg p-8"
      style={{ fontFamily: 'system-ui, -apple-system, "Segoe UI", Roboto, sans-serif' }}
    >
      <div className="flex items-start justify-between pb-5 border-b border-slate-200">
        <div>
          <div className="text-xl font-bold tracking-tight">{config.name}</div>
          <div className="text-sm text-slate-500 mt-0.5">Order Receipt</div>
        </div>
        <div className="text-right text-sm">
          <div className="font-semibold tabular-nums">{receiptNumberFor(row.id)}</div>
          <div className="text-slate-500">{formatDate(row.created_at)}</div>
        </div>
      </div>

      <div className="grid grid-cols-2 gap-8 mt-6">
        <div>
          <SectionTitle>Bill To</SectionTitle>
          <div className="text-sm text-slate-900 font-medium">{businessName}</div>
          {contactName ? <div className="text-sm text-slate-700">{contactName}</div> : null}
          {contactEmail ? <div className="text-sm text-slate-700">{contactEmail}</div> : null}
          {contactPhone ? <div className="text-sm text-slate-700">{contactPhone}</div> : null}
        </div>
        <div>
          <SectionTitle>Issued By</SectionTitle>
          {/* CTV-518: CTVBuyer is the merchant of record — it issues the
              receipt on the station's behalf. Station branding stays in the
              header above. */}
          <div className="text-sm text-slate-900 font-medium">CTVBuyer</div>
          <div className="text-sm text-slate-700">on behalf of {config.name}</div>
        </div>
      </div>

      <div className="mt-6 pt-5 border-t border-slate-200">
        <SectionTitle>Campaign</SectionTitle>
        <Row label="Name" value={row.campaign?.campaignName?.trim() || businessName} />
        <Row
          label="Dates"
          value={
            row.campaign?.startDate && row.campaign?.endDate
              ? `${formatDate(row.campaign.startDate)} – ${formatDate(row.campaign.endDate)}`
              : '—'
          }
        />
        <Row label={marketsLabel} value={marketsValue} />
        <Row label="Payment Plan" value={planLabel} />
      </div>

      <div className="mt-6 pt-5 border-t border-slate-200">
        <SectionTitle>Payment Summary</SectionTitle>
        <div className="flex justify-between text-sm py-1.5">
          <span className="text-slate-700">Media Spend</span>
          <span className="tabular-nums text-slate-900">${formatMoney(mediaSpend)}</span>
        </div>
        <div className="flex justify-between text-sm py-1.5">
          <span className="text-slate-700">Service fee</span>
          <span className="tabular-nums text-slate-900">${formatMoney(fee)}</span>
        </div>
        {tax > 0 ? (
          <div className="flex justify-between text-sm py-1.5">
            <span className="text-slate-700">Tax</span>
            <span className="tabular-nums text-slate-900">${formatMoney(tax)}</span>
          </div>
        ) : null}
        <div className="flex justify-between text-base font-semibold py-2 mt-1 border-t border-slate-200">
          <span>{isMonthly ? 'Total Commitment' : 'Total Authorized'}</span>
          <span className="tabular-nums">${formatMoney(total)}</span>
        </div>
        <div className="text-xs text-slate-500 mt-1">
          {isMonthly
            ? `No charge today. Billed across ${schedule.length || 'multiple'} payments after approval — see schedule below.`
            : upfrontSummaryNote(row.status, row.reviewed_at)}
        </div>
        {cardLine ? (
          <div className="text-xs text-slate-700 mt-2">{cardLine}</div>
        ) : null}
      </div>

      {/* CTV-516: upfront payments ledger — the initial charge plus each
          extension as its own dated, status-bearing row, ending in the actual
          total paid (original authorized + extensions that charged). Only
          shown once at least one extension exists; the single-payment case is
          fully described by the Payment Summary above. */}
      {!isMonthly && hasUpfrontExtensions && upfrontPayments ? (
        <div className="mt-6 pt-5 border-t border-slate-200">
          <SectionTitle>Payments</SectionTitle>
          {upfrontPayments.rows.map((p) => (
            <div key={p.key} className="flex justify-between text-sm py-1.5">
              <div>
                <div className="text-slate-900">{p.label}</div>
                <div className="text-xs text-slate-500 flex items-center gap-2">
                  <StatusPill status={p.status} />
                  {p.date ? <span>{formatDate(p.date)}</span> : null}
                </div>
              </div>
              <div className="tabular-nums text-slate-900">${formatMoney(p.amountCents / 100)}</div>
            </div>
          ))}
          <div className="flex justify-between text-base font-semibold py-2 mt-1 border-t border-slate-200">
            <span>Total Paid</span>
            <span className="tabular-nums">${formatMoney(upfrontPayments.totalCents / 100)}</span>
          </div>
        </div>
      ) : null}

      {isMonthly && schedule.length > 0 ? (
        <div className="mt-6 pt-5 border-t border-slate-200">
          <SectionTitle>Payment Schedule</SectionTitle>
          <div className="text-xs text-slate-500 mb-2">
            First payment is due on the campaign start date; subsequent payments charge on each anniversary date.
          </div>
          {schedule.map((s) => (
            <div key={s.key} className="flex justify-between text-sm py-1.5">
              <div>
                <div className="text-slate-900">{s.label}</div>
                <div className="text-xs text-slate-500">
                  {s.coversLabel}
                  {s.isPartial ? ' (prorated)' : ''}
                </div>
                <div className="text-xs text-slate-500 flex items-center gap-2 mt-0.5">
                  <StatusPill status={s.status} />
                  {s.date ? (
                    <span>{s.status === 'paid' ? 'Paid' : 'Due'} {formatDate(s.date)}</span>
                  ) : null}
                </div>
              </div>
              <div className="tabular-nums text-slate-900">${formatMoney(s.amountCents / 100)}</div>
            </div>
          ))}
        </div>
      ) : null}

      <div className="mt-6 pt-5 border-t border-slate-200 text-xs text-slate-600 leading-relaxed">
        <div className="font-semibold text-slate-700 mb-1">Authorization Notice</div>
        {isMonthly ? (
          <>
            By submitting, you've agreed to pay according to the schedule above. The first payment
            is due on the campaign start date after approval; subsequent payments will charge to
            the same card on each anniversary date. If the commercial is rejected, no charges will
            be made. If a future monthly charge is declined, we'll retry once before marking the
            campaign for payment recovery.
          </>
        ) : (
          <>
            Card authorized for the total above. You will only be charged once your commercial is
            approved. If the commercial is rejected, the hold is released and no charge will be
            made.
          </>
        )}
      </div>

      <div className="mt-4 text-[11px] text-slate-500 leading-relaxed">
        Charges appear as CTVBuyer on your card statement.{' '}
        Generated {formatDateTime(new Date().toISOString())}.
      </div>
    </div>
  );
};

export default OrderReceipt;
